<script>
export default {
	name: 'MHHPromotionPopUp',
	inheritAttrs: false,
};
</script>

<script setup>
import { Teleport } from 'vue';
import dayjs from 'dayjs';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import useDisableScroll from '~composables/useDisableScroll.js';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { getCookie, setCookie, deleteCookie } from '~/logic/helpers/cookies.js';
import CTAButton from '~/aem-components/CTAButton.vue';
import { sleep } from '~/logic/helpers/utils.js';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';
import usePersistencyWithExpiry from '~/logic/composables/usePersistencyWithExpiry';


const props = defineProps({
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	image: { type: String, default: '' },
	imageAltText: { type: String, default: '' },
	promotionBody: { type: String, default: '' },
	ctaButton1Label: { type: String, default: '' },
	ctaButton1Path: { type: String, default: '#' },
	ctaButton2Label: { type: String, default: '' },
	startDateTime: { type: String, default: null },
	endDateTime: { type: String, default: null },
	hiddenDuration: { type: String, default: 'next' }, // '0' | '30' | '60' | '180' | '360' | '720' | 'next'  <-- numbers are in minutes
	promotionPopUpTitle: { type: String, default: 'PROMOTION_POPUP_VISIBILITY' },
});

const COOKIE_ID = props.promotionPopUpTitle.replaceAll(' ', '_');
const { isEditorMode } = useGlobalAEMState();

const rootEl = ref(null);
const hasUserDismissed = ref(false);
const hasHiddenCookie = ref(false);
const hasUserDecidedSettings = ref(false);


const { hasFocus, activate: activeFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap(rootEl);

const { retrieveLocalStorage } = usePersistencyWithExpiry('cookies-notification:settings-decided');

const handleRootElOnMounted = async () => {
	rootEl.value?.focus();
	await nextTick(); // oddly enough, we have to use nextTick in order for focus trap to work 🤔
	activeFocusTrap();
};
const handleRootElBeforeUnmount = () => {
	deactivateFocusTrap();
	clearInterval(checkValidityInterval);
	clearInterval(checkVisibilityByConsent);
};


const getPopupExpiryDayjs = () => {
	if (props.hiddenDuration === 'once') {
		return dayjs().add(1, 'year');
	}

	if (props.hiddenDuration === 'next') {
		return dayjs().endOf('day');
	}

	const durationInMinutes = parseInt(props.hiddenDuration, 10);
	return durationInMinutes && dayjs().add(durationInMinutes, 'minute');
};

const dismissOverlayByUser = () => {
	hasUserDismissed.value = true;

	if (getPopupExpiryDayjs()) {
		const expiryDateString = getPopupExpiryDayjs().toDate().toUTCString();
		setCookie(COOKIE_ID, 'hidden', expiryDateString);
	}
};

if (getCookie(COOKIE_ID)) {
	hasHiddenCookie.value = true;
	
	// Delete promo-popup-cookie when props.hiddenDuration is set as 0
	if (parseInt(props.hiddenDuration, 10) === 0) {
		deleteCookie(COOKIE_ID);
		hasHiddenCookie.value = false;
	}
}


// ------------------------------------ Timing Logic ------------------------------------
/* Compute validity and visibility by comparing against props.startDateTime & props.endDateTime */
const isNowValidPromoTime = ref(dayjs().isBetween(
	dayjs(props.startDateTime),
	dayjs(props.endDateTime),
	'second',
	'[]',
));
const checkValidityInterval = setInterval(() => {
	isNowValidPromoTime.value = dayjs().isBetween(
		dayjs(props.startDateTime),
		dayjs(props.endDateTime),
		'second',
		'[]',
	);
}, 1000);
if (dayjs().isSameOrAfter(props.endDateTime, 'second')) {
	// no chance of this pop-up ever showing
	clearInterval(checkValidityInterval);
}
// ------------------------------------ END Timing Logic ------------------------------------

const checkVisibilityByConsent = setInterval(() => {
	if (!hasUserDecidedSettings.value) {
		hasUserDecidedSettings.value = retrieveLocalStorage()?.hasUserDecidedSettings ?? false;
	}
}, 1000);

if (hasUserDecidedSettings.value) {
	clearInterval(checkVisibilityByConsent);
}

// stop check the popup visibility when cookie consent has been decided.
watch(hasUserDecidedSettings, (newValue) => {
	if (newValue) {
		clearInterval(checkVisibilityByConsent);
	}
});



const isPopUpVisibleComputed = computed(() => {
	if (isEditorMode.value) return true;
	if (hasUserDismissed.value) return false;
	
	const authorConfiguredHidden = isMobileViewport.value ? (props.isHideInMobile) : (props.isHideInDesktop);
	
	return (
		authorConfiguredHidden === false &&
		hasHiddenCookie.value === false &&
		isNowValidPromoTime.value &&
		hasUserDecidedSettings.value
	);
});

if (!isEditorMode.value) {
	// due to multiple 'useDisableScroll' is in the page, it messes up with the scrollbar
	onMounted(async () => {
		await sleep(500); // <-- this is a workaround / hack to force the below 'useDisableScroll' statement to take effect later
		useDisableScroll(isPopUpVisibleComputed);
	});
}

</script>



<template>
<template v-if="isEditorMode && (props.isHideInDesktop || props.isHideInMobile)">
	<div class="generic-container py-10 text-center">
		<p>Hidden in: {{ [
			(props.isHideInDesktop ? 'Desktop' : null),
			(props.isHideInMobile ? 'Mobile' : null),
		].filter(Boolean).join(', ') }}</p>
	</div>
</template>

<component
	:is="isEditorMode ? 'div' : Teleport"
	to="body"
>
	<Transition
		name="overlay-fade-and-zoom"
		appear
	>
		<div
			v-if="isPopUpVisibleComputed"
			ref="rootEl"
			class="MHHPromotionPopUp backdrop"
			:class="{
				'!relative': isEditorMode,
			}"
			v-bind="$attrs"
			tabindex="-1"
			@keydown.esc.capture="dismissOverlayByUser"
			@vue:mounted="handleRootElOnMounted"
			@vue:beforeUnmount="handleRootElBeforeUnmount"
			@click.self="dismissOverlayByUser"
		>
			<div class="overlay-container">
				<div class="overlay-header">
					<button
						type="button"
						class="absolute top-4 right-4"
						aria-label="Popup close button"
						@click="dismissOverlayByUser"
					>
						<icon-far-xmark class="w-6 h-6" />
					</button>
					<div class="overlay-header-image w-full">
						<img
							class="w-full h-full object-center object-cover"
							:src="props.image"
							:alt="props.imageAltText"
						/>
					</div>
				</div>
				<div class="overlay-body px-6 mt-4">
					<div v-html-sanitize="props.promotionBody" class="author-html-container line-clamp-5"></div>
				</div>
				<div class="overlay-footer px-6 mt-6 mb-10">
					<AppHyperlink
						:href="props.ctaButton1Path"
						class="flex-grow basis-0 hover:no-underline"
					>
						<CTAButton
							:ctaText="props.ctaButton1Label"
							sizeType="small"
							ctaSizing="fluid"
							styleType="primary"
							containerClass="w-full"
							tabindex="-1"
						/>
					</AppHyperlink>
					<CTAButton
						:ctaText="props.ctaButton2Label"
						sizeType="small"
						ctaSizing="fluid"
						styleType="secondary"
						containerClass="flex-grow basis-0"
						@click="dismissOverlayByUser"
					/>
				</div>
			</div>
		</div>
	</Transition>
</component>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.MHHPromotionPopUp {
	&[mhhpromopopupstyle='style2'] {
		.overlay-container {
			max-width: 588px;
		}
	}
}

.backdrop {
	position: fixed;
	z-index: 10000;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: color.change(black, $alpha: 0.8);
	
	display: flex;
	justify-content: flex-end;
	transition: opacity 0.45s ease;
}

.overlay-container {
	background-color: var(--overlay-container-bg-color, white);
	max-width: 408px;
	width: 100%;
	margin: auto;
	overflow: hidden;
	@apply rounded-3xl;
}

.overlay-header {
	position: relative;
}
.overlay-header-image {
	aspect-ratio: 3/2;
}

.overlay-body {
	
}

.overlay-footer {
	display: flex;
	align-items: center;
	justify-content: center;
	@apply gap-5;
	
	@media #{var.$query-max-md} {
		flex-direction: column;
		@apply gap-4;
	}
}

// animations
.overlay-fade-and-zoom-enter-from,
.overlay-fade-and-zoom-leave-to {
	opacity: 0;
}

.overlay-fade-and-zoom-enter-active,
.overlay-fade-and-zoom-leave-active {
	transition: opacity 200ms;
}

.overlay-fade-and-zoom-enter-from,
.overlay-fade-and-zoom-leave-to {
	.overlay-container {
		transform: scale(1.2);
	}
}

.overlay-fade-and-zoom-enter-active,
.overlay-fade-and-zoom-leave-active {
	.overlay-container {
		transition: transform 200ms;
	}
}

</style>
